import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ApplicationOverview from "../../components/AdminPortal/ApplicationOverview";
import ApplicationTable from "../../components/AdminPortal/ApplicationTable";
import ApprovedApplicationTable from "../../components/AdminPortal/ProcessingTable";
import RecentActivity from "../../components/AdminPortal/RecentActivity";
import StaffManagement from "../../components/AdminPortal/StaffManagement";
import StatCards from "../../components/AdminPortal/StatCards";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import { dashboardAPI } from "../../services/AdminPortal/dashboardAPI";
import { useNavigate } from "react-router-dom";
import { staffAPI } from "../../services/AdminPortal/staffAPI";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { formatDate } from "../../utils/utils";
import { useAuth } from "../../context/AuthContext";
import ConfirmationDialog from "../../components/StaffPortal/ConfirmationDialog.jsx";
import { salesAPI } from "../../services/AdminPortal/salesAPI"; // Import salesAPI

const Dashboard = () => {
  const navigate = useNavigate();
  const { sidebarExpanded, isMobile } = useSidebar();
  const [staff, setStaff] = useState([]);
  const [activeApplications, setActiveApplications] = useState([]);
  const [approvedApplications, setApprovedApplications] = useState([]);
  const [rejectedApplications, setRejectedApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminSalesData, setAdminSalesData] = useState(null);
  const [loadingAdminSales, setLoadingAdminSales] = useState(true);
  const [adminSalesError, setAdminSalesError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const adminId = user.id;
  const [isAssigning, setIsAssigning] = useState(null); // State to track which applicant is being assigned (stores applicant._id)

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [applicantToAssign, setApplicantToAssign] = useState(null); // State to hold the applicant being assigned
  const [staffToAssign, setStaffToAssign] = useState(null);
  const getStaff = async () => {
    try {
      const staff = await staffAPI.getByRole("staff");
      setStaff(staff.data || []); // Ensure staff is an array
    } catch (error) {
      console.error("Error fetching applicants:", error);
    }
  };
  const openAssignmentModal = (applicant, staffMember) => {
    setApplicantToAssign(applicant);
    setStaffToAssign(staffMember);
    setIsModalOpen(true);
  };
  const handleConfirmAssignment = async () => {
    if (!applicantToAssign || !staffToAssign) return;
    setIsAssigning(applicantToAssign._id); // Set assigning state to the applicant's ID

    setLoading(true); // Show loading while assigning
    try {
      // Call the API to update the backend
      await crmAPI.assignLeadToStaff(applicantToAssign._id, staffToAssign._id);

      // Update the local state to reflect the change immediately
      setApplicants((prevApplicants) =>
        prevApplicants.map((app) =>
          app._id === applicantToAssign._id
            ? { ...app, assignedTo: staffToAssign }
            : app
        )
      );
    } catch (error) {
      console.error(
        "Error assigning staff:",
        error.response?.data || error.message
      );
      alert(
        `Failed to assign staff: ${
          error.response?.data?.message || error.message
        }`
      ); // Show alert on error
    } finally {
      // setLoading(false); // Reset main loading state if it was used

      setIsAssigning(null); // Reset assigning state

      // Close the modal and clear state
      setIsModalOpen(false);
      setApplicantToAssign(null);
      setStaffToAssign(null);
    }
  };
  const fetchActiveApplications = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getActiveApplications();

      // Filter applications based on status
      const submittedApplications = response.filter(
        (app) =>
          app.status.toLowerCase() === "submitted" ||
          app.status.toLowerCase() === "in-review" ||
          app.status.toLowerCase() === "processing" ||
          app.status.toLowerCase() === "new-leads"
      );

      setActiveApplications(submittedApplications);
    } catch (error) {
      console.error("Error fetching active applications:", error);
      setError("Failed to fetch active applications");
      toast.error("Failed to fetch active applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedApplications = async () => {
    try {
      const response = await dashboardAPI.getApprovedApplications();

      // Filter applications based on status
      const approvedApps = response.filter(
        (app) => app.status.toLowerCase() === "approved"
      );

      setApprovedApplications(approvedApps);
    } catch (error) {
      console.error("Error fetching approved applications:", error);
      toast.error("Failed to fetch approved applications");
    }
  };

  const fetchRejectedApplications = async () => {
    try {
      const response = await dashboardAPI.getActiveApplications(); // Using the same endpoint

      // Filter applications based on status
      const rejectedApps = response.filter(
        (app) => app.status.toLowerCase() === "rejected"
      );

      setRejectedApplications(rejectedApps);
    } catch (error) {
      console.error("Error fetching rejected applications:", error);
      toast.error("Failed to fetch rejected applications");
    }
  };

  // Fetch Admin Sales Data (without filter - get all data)
  const fetchAdminSalesData = async () => {
    try {
      setLoadingAdminSales(true);
      setAdminSalesError(null);
      const response = await salesAPI.getAdminSalesData();
      if (response.success) {
        setAdminSalesData(response.data);
      } else {
        throw new Error(response.message || "Failed to fetch admin sales data");
      }
    } catch (err) {
      console.error("Error fetching admin sales data:", err);
      setAdminSalesError(err.message || "Failed to fetch admin sales data");
      toast.error(err.message || "Could not load admin sales data.");
    } finally {
      setLoadingAdminSales(false);
    }
  };

  // Filter the data based on selected filter
  const getFilteredData = () => {
    if (selectedFilter === "all" && adminSalesData?.currentPeriod) {
      return adminSalesData.currentPeriod;
    }
    
    // Calculate filtered stats from application data
    const now = new Date();
    let startDate;
    
    switch (selectedFilter) {
      case "monthly":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "quarterly":
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1); // Last 3 months
        break;
      case "yearly":
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()); // Last 12 months
        break;
      default:
        return adminSalesData?.currentPeriod || {};
    }
    
    const allApps = [
      ...(activeApplications || []),
      ...(approvedApplications || []),
      ...(rejectedApplications || [])
    ];
    
    const filteredApps = allApps.filter(app => {
      if (!app.createdAt) return false;
      const appDate = new Date(app.createdAt);
      const isInRange = appDate >= startDate;
      console.log('App date:', appDate, 'Start date:', startDate, 'In range:', isInRange);
      return isInRange;
    });
    
    const totalApplications = filteredApps.length;
    const pendingApplications = filteredApps.filter(app => 
      ["submitted", "in-review", "processing", "new-leads"].includes(app.status?.toLowerCase())
    ).length;
    // Debug: Check what statuses exist
    const allStatuses = [...new Set(filteredApps.map(app => app.status))];
    console.log('Available statuses:', allStatuses);
    
    const approvedApps = filteredApps.filter(app => {
      const status = app.status?.toLowerCase();
      const isApproved = status === "approved" || status === "disbursed" || status === "completed";
      if (isApproved) console.log('Found approved app:', app._id, 'Status:', app.status);
      return isApproved;
    });
    
    // Calculate total loan amount from approved apps in the filtered period
    const totalLoanAmount = approvedApps.reduce((sum, app) => {
      const amount = parseFloat(app.principle) || parseFloat(app.loanAmount) || parseFloat(app.amount) || 0;
      console.log('App:', app._id, 'Amount:', amount, 'Principle:', app.principle, 'Status:', app.status);
      return sum + amount;
    }, 0);
    
    console.log('Filter:', selectedFilter, 'StartDate:', startDate, 'AllApps:', allApps.length, 'FilteredApps:', filteredApps.length, 'ApprovedApps:', approvedApps.length, 'TotalAmount:', totalLoanAmount);
    
    // If no approved apps found, use the approvedApplications array directly
    let finalApprovedCount = approvedApps.length;
    let finalLoanAmount = totalLoanAmount;
    
    if (approvedApps.length === 0 && selectedFilter === 'all') {
      // For "all" filter, use the original API data
      finalApprovedCount = adminSalesData?.currentPeriod?.approvedApplications || 0;
      finalLoanAmount = adminSalesData?.currentPeriod?.totalLoanAmount || 0;
    }
    
    return {
      totalApplications,
      pendingApplications,
      approvedApplications: finalApprovedCount,
      totalLoanAmount: finalLoanAmount
    };
  };

  // Handle filter change from StatCards
  const handleFilterChange = (newFilter) => {
    setSelectedFilter(newFilter);
    // Data will be filtered in getFilteredData() function
  };

  useEffect(() => {
    const hasLoggedIn = sessionStorage.getItem("justLoggedIn");
    if (hasLoggedIn === "true") {
      toast.success("Login successful");
      sessionStorage.removeItem("justLoggedIn"); // Prevent repeat on refresh
    }

    getStaff();
    fetchActiveApplications();
    fetchApprovedApplications();
    fetchRejectedApplications();
    fetchAdminSalesData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#131313] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#131313] text-white flex items-center justify-center">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Error Loading Dashboard</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Combine all applications for the overview
  const allApplications = [
    ...activeApplications,
    ...approvedApplications,
    ...rejectedApplications,
  ];

  const handleViewApplication = (id) => {
    navigate(`/admin/loan-applications/approval/${id}`);
  };
  const handleDownloadProfile = async (id) => {
    try {
      const application = activeApplications.find((app) => app._id === id);
      if (!application) throw new Error("Application not found");
      const mainZip = new JSZip();

      // Details sheet data
      const detailsData = [
        {
          "Applicant Name": application?.user?.name || "",
          "Application ID": application?._id || "",
          "Applied On": formatDate(application?.createdAt) || "",
          "Assigned To": application?.assignedTo?.name || "",
          Status: application?.status || "",
          "Loan Amount": application?.principle || "",
          "Loan Type": application?.loanType || "",
        },
      ];
      const detailsWorkbook = XLSX.utils.book_new();
      const detailsSheet = XLSX.utils.json_to_sheet(detailsData);
      XLSX.utils.book_append_sheet(detailsWorkbook, detailsSheet, "Details");
      const detailsExcelBuffer = XLSX.write(detailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("Details.xlsx", detailsExcelBuffer);

      // PersonalDetails sheet data
      const personalDetailsData = [
        {
          "Full Name": application?.user?.name || "",
          "Phone Number": application?.user?.phoneNumber || "",
          "Email ID": application?.user?.email || "",
          Street: application?.user?.address?.street || "",
          City: application?.user?.address?.city || "",
          State: application?.user?.address?.state || "",
          Pincode: application?.user?.address?.pincode || "",
        },
      ];
      const personalDetailsWorkbook = XLSX.utils.book_new();
      const personalSheet = XLSX.utils.json_to_sheet(personalDetailsData);
      XLSX.utils.book_append_sheet(
        personalDetailsWorkbook,
        personalSheet,
        "PersonalDetails"
      );
      const personalDetailsExcelBuffer = XLSX.write(personalDetailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("PersonalDetails.xlsx", personalDetailsExcelBuffer);

      // FinancialDetails sheet data
      const financialDetailsData = [
        {
          "Credit Score": 750,
          "Credit Limit": "₹3,00,000",
          "Monthly Income": "₹80,000",
          "Business Name": "IoT Business",
          "Business Type": "Private",
          "Years in Business": "40 years",
          "Existing EMI's": "1 Active Loan (₹60,000)",
        },
      ];
      const financialDetailsWorkbook = XLSX.utils.book_new();
      const financialSheet = XLSX.utils.json_to_sheet(financialDetailsData);
      XLSX.utils.book_append_sheet(
        financialDetailsWorkbook,
        financialSheet,
        "FinancialDetails"
      );
      const financialDetailsExcelBuffer = XLSX.write(financialDetailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("FinancialDetails.xlsx", financialDetailsExcelBuffer);

      // LoanDetails sheet data
      const loanDetailsData = [
        {
          "Requested Amount": application?.principle || "",
          "Monthly EMI": application?.emi || "",
          "Interest Rate": application?.interest || "",
          "Tenure Requested": application?.monthsToRepay || "",
          "Loan Type": application?.loanType || "",
          "Processing Fee": application?.processingFee || "",
          "Insurance Fees": application?.insuranceFee || "",
          "Applied Insurance": application?.appliedInsurance || "",
          "Platform Fees": application?.platformFee || "",
        },
      ];
      const loanDetailsWorkbook = XLSX.utils.book_new();
      const loanSheet = XLSX.utils.json_to_sheet(loanDetailsData);
      XLSX.utils.book_append_sheet(
        loanDetailsWorkbook,
        loanSheet,
        "LoanDetails"
      );
      const loanDetailsExcelBuffer = XLSX.write(loanDetailsWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      mainZip.file("LoanDetails.xlsx", loanDetailsExcelBuffer);

      // SubmittedDocuments Info sheet data (metadata)
      const submittedDocsData = (application.documents || []).map((doc) => ({
        "Document Type": doc.documentType || "",
        "Document Number": doc.documentNumber || "",
        "Upload Date": formatDate(doc.uploadDate) || "",
        Status: doc.status || "",
      }));
      const submittedDocsInfoWorkbook = XLSX.utils.book_new();
      const submittedDocsSheet = XLSX.utils.json_to_sheet(submittedDocsData);
      XLSX.utils.book_append_sheet(
        submittedDocsInfoWorkbook,
        submittedDocsSheet,
        "SubmittedDocumentsInfo"
      );
      const submittedDocsInfoExcelBuffer = XLSX.write(
        submittedDocsInfoWorkbook,
        { bookType: "xlsx", type: "array" }
      );
      mainZip.file("SubmittedDocumentsInfo.xlsx", submittedDocsInfoExcelBuffer);

      // --- Create a separate zip for documents ---
      if (application.documents && application.documents.length > 0) {
        const documentsZip = new JSZip();
        let documentsAdded = 0;

        // Helper to fetch and add file to the documentsZip
        const addDocumentToZip = async (doc) => {
          // IMPORTANT ASSUMPTION:
          // Each 'doc' object has a 'url' property with a direct link to the document file.
          // And optionally a 'fileName' property for the original file name.
          // Adjust this part if your document data structure is different.
          if (!doc.url) {
            console.warn("Document skipped: URL is missing.", doc);
            return;
          }

          try {
            const response = await fetch(doc.url);
            if (!response.ok) {
              console.error(
                `Failed to fetch document ${doc.fileName || doc.url}: ${
                  response.statusText
                }`
              );
              return; // Skip this document
            }
            const fileBlob = await response.blob();

            // Determine filename
            let fileName = doc.fileName;
            if (!fileName) {
              const urlPath = new URL(doc.url).pathname;
              fileName =
                urlPath.substring(urlPath.lastIndexOf("/") + 1) ||
                `${doc.documentType?.replace(/\s+/g, "_") || "document"}_${
                  doc.documentNumber || Date.now()
                }`;
              // Ensure a fallback extension if not in parsed filename
              if (!fileName.includes(".")) {
                const extensionMatch = doc.url.match(
                  /\.([0-9a-z]+)(?:[\?#]|$)/i
                );
                fileName += extensionMatch ? `.${extensionMatch[1]}` : ".file";
              }
            }
            documentsZip.file(fileName, fileBlob);
            documentsAdded++;
          } catch (docError) {
            console.error(
              `Error processing document ${doc.fileName || doc.url}:`,
              docError
            );
          }
        };
        await Promise.all(
          (application.documents || []).map((doc) => addDocumentToZip(doc))
        );
        if (documentsAdded > 0) {
          const documentsZipBlob = await documentsZip.generateAsync({
            type: "blob",
          });
          mainZip.file("Documents.zip", documentsZipBlob);
        }
      }

      // Generate zip and trigger download
      const content = await mainZip.generateAsync({ type: "blob" });
      const blobUrl = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${application?.user?.name.replace(
        /\s+/g,
        "_"
      )}_profile.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading user profile:", error);
      alert("Failed to download user profile. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <TopNavbar />

      <div className="p-2 sm:p-4 md:p-6">
        {/* Full width stat cards */}
        <div className="w-full">
          <StatCards
            currentPeriodData={getFilteredData()}
            trendsData={adminSalesData?.trends}
            loading={loadingAdminSales}
            onFilterChange={handleFilterChange}
            selectedFilter={selectedFilter}
          />
        </div>

        {/* Make flexible layout for mobile */}
        <div className="flex flex-col lg:flex-row gap-2 sm:gap-4 mt-4 sm:mt-6 mb-4 sm:mb-6 w-full">
          <div className="w-full ">
            <ApplicationOverview applications={allApplications} />
          </div>
        </div>

        {/* Full width tables with overflow handling */}
        <div className="mb-4 sm:mb-6 p-1 sm:p-2 w-full overflow-x-auto">
          <ApplicationTable
            loading={loading}
            staff={staff}
            isAssigning={isAssigning} // Pass the assigning state
            openAssignmentModal={openAssignmentModal}
            adminId={adminId}
            handleDownloadProfile={handleDownloadProfile}
            setActiveApplications={setActiveApplications}
            handleViewApplication={handleViewApplication}
            activeApplications={activeApplications}
            userRole={user.role}
          />
        </div>

        <div className="mb-4 sm:mb-6 p-1 sm:p-2 w-full overflow-x-auto">
          <ApprovedApplicationTable
            approvedApplications={approvedApplications}
            onStatusUpdate={fetchApprovedApplications}
            userRole={user.role}
          />
        </div>

        <div className="mb-4 sm:mb-6 p-1 sm:p-2 w-full overflow-x-auto">
          <StaffManagement staffMembers={staff} userRole={user.role} />
        </div>
        <ConfirmationDialog
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmAssignment} // Use the new confirmation handler
          title="Confirm Staff Assignment"
          message={`Are you sure you want to assign this applicant to ${
            staffToAssign?.name || "selected staff"
          }?`}
        />
      </div>
    </div>
  );
};

export default Dashboard;
